import { useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc, addDoc, collection, serverTimestamp, setDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';

// When logged in:  score lives in Firestore, each answer written to quizHistory subcollection
// When guest:      score is passed in from usePersist (localStorage), no Firestore writes
export function useQuizHistory(user, guestScore, setGuestScore) {
  const [cloudScore, setCloudScore] = useState(null);

  useEffect(() => {
    if (!user) {
      setCloudScore(null);
      return;
    }
    // Subscribe to the user's summary doc for real-time score updates
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setCloudScore(snap.data().quizScore ?? { correct: 0, total: 0 });
      } else {
        // First time this user has logged in — create their doc
        setDoc(ref, { quizScore: { correct: 0, total: 0 } });
        setCloudScore({ correct: 0, total: 0 });
      }
    });
    return unsub;
  }, [user]);

  // The score to display — cloud if logged in, localStorage if guest
  const score = user ? (cloudScore ?? { correct: 0, total: 0 }) : guestScore;

  // Called by Quiz after each answered question
  const logAnswer = async ({ type, question, correctAnswer, tolerance, unit }, userAnswer, correct) => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const historyRef = collection(db, 'users', user.uid, 'quizHistory');

      // Write the full question record to history
      await addDoc(historyRef, {
        type,
        question,
        correctAnswer,
        userAnswer,
        tolerance,
        unit,
        correct,
        answeredAt: serverTimestamp(),
      });

      // Update the summary score atomically — server-side increment avoids
      // last-write-wins races when answers are submitted in rapid succession.
      await updateDoc(userRef, {
        'quizScore.total':   increment(1),
        'quizScore.correct': increment(correct ? 1 : 0),
      });
    } else {
      // Guest: update localStorage score
      setGuestScore((s) => ({
        correct: s.correct + (correct ? 1 : 0),
        total: s.total + 1,
      }));
    }
  };

  const resetScore = async () => {
    if (user) {
      await updateDoc(doc(db, 'users', user.uid), {
        'quizScore.total': 0,
        'quizScore.correct': 0,
      });
    } else {
      setGuestScore({ correct: 0, total: 0 });
    }
  };

  return { score, logAnswer, resetScore, loading: user && cloudScore === null };
}
